import { z } from 'zod';

// Base schemas for common entities
export const BaseEntitySchema = z.object({
  id: z.string().min(1),
  createdAt: z.date().or(z.string().transform(str => new Date(str))),
  updatedAt: z.date().or(z.string().transform(str => new Date(str))),
  tenantId: z.string().optional(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional()
});

// User schema
export const UserSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  email: z.string().email(),
  roles: z.array(z.string()),
  orgType: z.enum(['internal', 'county', 'vendor']),
  orgId: z.string(),
  vendor: z.string().optional(),
  token: z.string().optional(),
  userType: z.enum(['admin', 'fund_manager', 'limited_partner', 'investor']).optional(),
  preferences: z.object({
    reportFrequency: z.enum(['daily', 'weekly', 'monthly']).optional(),
    alertTypes: z.array(z.string()).optional(),
    dataAccess: z.enum(['basic', 'enhanced']).optional()
  }).optional()
});

// Project schema
export const ProjectSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  status: z.enum(['active', 'planning', 'on-hold', 'completed', 'cancelled', 'executing']),
  health: z.enum(['healthy', 'at-risk', 'critical', 'on-track']),
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  progress: z.number().min(0).max(100),
  budget: z.number().min(0),
  spent: z.number().min(0),
  team: z.object({
    lead: z.string(),
    members: z.number().min(0)
  }),
  timeline: z.object({
    startDate: z.date().or(z.string().transform(str => new Date(str))),
    finishDate: z.date().or(z.string().transform(str => new Date(str))),
    currentDate: z.date().or(z.string().transform(str => new Date(str))),
    statusDate: z.date().or(z.string().transform(str => new Date(str))),
    milestones: z.array(z.object({
      id: z.string(),
      name: z.string(),
      date: z.date().or(z.string().transform(str => new Date(str))),
      completed: z.boolean()
    }))
  }),
  clientId: z.string().optional(),
  clientName: z.string().optional(),
  riskScore: z.number().min(0).max(10).optional()
});

// Support Ticket schema
export const TicketSchema = z.object({
  id: z.string().min(1),
  globalTicketNumber: z.string().min(1),
  title: z.string().min(1),
  description: z.string(),
  status: z.enum(['open', 'in-progress', 'escalated', 'resolved', 'closed']),
  priority: z.enum(['low', 'medium', 'high', 'urgent']),
  clientId: z.string().min(1),
  clientName: z.string().min(1),
  clientTier: z.enum(['standard', 'premium', 'enterprise', 'vip']),
  assignedAnalyst: z.string().optional(),
  createdAt: z.date().or(z.string().transform(str => new Date(str))),
  updatedAt: z.date().or(z.string().transform(str => new Date(str))),
  estimatedResolution: z.date().or(z.string().transform(str => new Date(str))).optional(),
  colorCode: z.enum(['🔴', '🟡', '🔵', '⚪', '🟢']),
  sentimentScore: z.number().min(0).max(1).optional(),
  frustrationLevel: z.enum(['low', 'medium', 'high', 'extreme']).optional(),
  escalationLevel: z.number().min(0),
  tags: z.array(z.string()),
  systemImpact: z.enum(['low', 'medium', 'high', 'critical']).optional(),
  businessCritical: z.boolean(),
  projectId: z.string().optional(), // Link to project
  relatedTickets: z.array(z.string()).optional()
});

// Workflow schema
export const WorkflowSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  type: z.enum(['project', 'support', 'cross-system']),
  status: z.enum(['active', 'paused', 'completed', 'cancelled']),
  steps: z.array(z.object({
    id: z.string(),
    name: z.string(),
    type: z.enum(['action', 'decision', 'wait', 'notification']),
    order: z.number(),
    conditions: z.record(z.string(), z.any()).optional(),
    actions: z.array(z.object({
      type: z.string(),
      target: z.string(),
      parameters: z.record(z.string(), z.any()).optional()
    }))
  })),
  triggers: z.array(z.object({
    eventType: z.string(),
    conditions: z.record(z.string(), z.any()).optional()
  })),
  metadata: z.record(z.string(), z.any()).optional()
});

// Analytics schema
export const AnalyticsSchema = z.object({
  id: z.string().min(1),
  type: z.enum(['project', 'support', 'cross-system']),
  metrics: z.record(z.string(), z.any()),
  timeRange: z.object({
    start: z.date().or(z.string().transform(str => new Date(str))),
    end: z.date().or(z.string().transform(str => new Date(str)))
  }),
  filters: z.record(z.string(), z.any()).optional(),
  generatedAt: z.date().or(z.string().transform(str => new Date(str)))
});

// Validation functions
export class DataValidator {
  // Validate user data
  static validateUser(data: unknown): z.infer<typeof UserSchema> {
    return UserSchema.parse(data);
  }

  // Validate project data
  static validateProject(data: unknown): z.infer<typeof ProjectSchema> {
    return ProjectSchema.parse(data);
  }

  // Validate ticket data
  static validateTicket(data: unknown): z.infer<typeof TicketSchema> {
    return TicketSchema.parse(data);
  }

  // Validate workflow data
  static validateWorkflow(data: unknown): z.infer<typeof WorkflowSchema> {
    return WorkflowSchema.parse(data);
  }

  // Validate analytics data
  static validateAnalytics(data: unknown): z.infer<typeof AnalyticsSchema> {
    return AnalyticsSchema.parse(data);
  }

  // Safe validation with error handling
  static safeValidate<T>(schema: z.ZodSchema<T>, data: unknown): { success: true; data: T } | { success: false; errors: string[] } {
    try {
      const validatedData = schema.parse(data);
      return { success: true, data: validatedData };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors = (error as z.ZodError).errors.map(err => `${err.path.join('.')}: ${err.message}`);
        return { success: false, errors };
      }
      return { success: false, errors: ['Unknown validation error'] };
    }
  }

  // Validate cross-system data consistency
  static validateCrossSystemConsistency(
    projectData: unknown,
    ticketData: unknown
  ): { success: boolean; errors: string[] } {
    const errors: string[] = [];
    
    try {
      const project = ProjectSchema.parse(projectData);
      const ticket = TicketSchema.parse(ticketData);
      
      // Check client consistency
      if (project.clientId && ticket.clientId && project.clientId !== ticket.clientId) {
        errors.push('Client ID mismatch between project and ticket');
      }
      
      // Check project ID in ticket
      if (ticket.projectId && ticket.projectId !== project.id) {
        errors.push('Project ID mismatch in ticket');
      }
      
      return { success: errors.length === 0, errors };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const validationErrors = (error as z.ZodError).errors.map(err => `${err.path.join('.')}: ${err.message}`);
        return { success: false, errors: validationErrors };
      }
      return { success: false, errors: ['Unknown validation error'] };
    }
  }
}

// Type exports for use in components
export type User = z.infer<typeof UserSchema>;
export type Project = z.infer<typeof ProjectSchema>;
export type Ticket = z.infer<typeof TicketSchema>;
export type Workflow = z.infer<typeof WorkflowSchema>;
export type Analytics = z.infer<typeof AnalyticsSchema>;